import { type StarterChannelRow, starterChannelRows } from './starterChannelRows';

export type StepId = 'connect-channel' | 'create-portfolio' | 'add-content';
export type StepStatus = 'done' | 'idle';
export type ChecklistStep = { id: StepId; status: StepStatus };

export type Checklist = {
  /** Always length 3, always in `StepId` order — the card renders it positionally. */
  steps: readonly ChecklistStep[];
  doneCount: number;
  resolved: boolean;
  allComplete: boolean;
  rows: readonly StarterChannelRow[];
};

/**
 * The whole checklist, derived from live data on every render. Nothing about progress is stored,
 * so the card cannot disagree with what the team has actually done.
 */
export function starterChecklist(input: {
  integrations: readonly any[] | null | undefined;
  hasPortfolio: boolean | null;
  hasContent: boolean | null;
}): Checklist {
  const { integrations, hasPortfolio, hasContent } = input;

  const rows = starterChannelRows(integrations);

  // Every query has answered. `Array.isArray` is the test, not truthiness: an errored query and
  // an in-flight one both hand over `undefined`, and neither means "no integrations".
  const resolved = Array.isArray(integrations) && hasPortfolio !== null && hasContent !== null;

  const steps: readonly ChecklistStep[] = [
    { id: 'connect-channel', status: rows.some((row) => row.state === 'connected') ? 'done' : 'idle' },
    { id: 'create-portfolio', status: hasPortfolio === true ? 'done' : 'idle' },
    // A portfolio is created empty (`CreatePortfolio.tsx` sends `contents: []`), so this is a
    // genuinely separate step rather than one that ticks alongside the one above it.
    { id: 'add-content', status: hasContent === true ? 'done' : 'idle' },
  ];

  return {
    steps,
    doneCount: steps.filter((step) => step.status === 'done').length,
    resolved,
    allComplete: resolved && steps.every((step) => step.status === 'done'),
    rows,
  };
}
